/**
 * Registry of known tenant-level settings.
 * Each entry defines a setting that can be stored in the MD Settings table
 * with Category = 'Tenant.Config' and no UserId (tenant-wide).
 *
 * To add a new setting: add an entry here, and it will appear in the admin UI.
 */

type SettingSection = 'general' | 'legacy';

interface TenantSettingDefinition {
  /** Display label (translation key) */
  label: string;
  /** Input type for the admin UI */
  type: 'text' | 'boolean' | 'image';
  /** Default value when not set in DB */
  defaultValue: string;
  /** Description shown in the admin UI */
  description: string;
  /** Legacy window global name (e.g. sx_Foo) — set by init.ts for PF3 compatibility */
  windowGlobal?: string;
  /** Placeholder shown in the input when empty (e.g. example values) */
  placeholder?: string;
  /** Section grouping in the admin UI */
  section?: SettingSection;
}

export const TENANT_SETTINGS_REGISTRY: Record<string, TenantSettingDefinition> = {
  COMPANY_LOGO: {
    label: 'Company Logo',
    type: 'image',
    defaultValue: '',
    description: 'Base64 company logo used in PDF exports (Laytime, Estimator reports)'
  },
  FULLSTORY_ENABLED: {
    label: 'FullStory',
    type: 'boolean',
    defaultValue: 'true',
    description: 'Enable FullStory session recording for this tenant'
  },
  ENABLE_BALTIC_QUESTIONNAIRES_IMPORT: {
    label: 'Baltic Questionnaires Import',
    type: 'boolean',
    defaultValue: 'false',
    description: 'Show vessel particulars fields for Baltic Exchange questionnaire data import',
    windowGlobal: 'sx_EnableBalticQuestionnairesImport',
    section: 'legacy'
  },
  SEND_BUNKERING_TO_PLAN: {
    label: 'Send Bunkering to Plan',
    type: 'boolean',
    defaultValue: 'false',
    description: 'Automatically send bunkering data to voyage plan',
    windowGlobal: 'sx_SendBunkeringToPlan',
    section: 'legacy'
  },
  BUNKERING_PURCHASE_REPORT_PATH: {
    label: 'Bunkering Purchase Report Path',
    type: 'text',
    defaultValue: '',
    description: 'Path to the bunkering purchase report template',
    windowGlobal: 'sx_BunkeringPurchaseReportPath',
    placeholder: 'e.g. /sx/forms/tracker/reports/bunker-purchase-template',
    section: 'legacy'
  },
  BUNKERING_CONFIRMATION_REPORT_PATH: {
    label: 'Bunkering Confirmation Report Path',
    type: 'text',
    defaultValue: '',
    description: 'Path to the bunkering stem confirmation report template',
    windowGlobal: 'sx_BunkeringConfirmationReportPath',
    placeholder: 'e.g. /sx/forms/tracker/reports/stem-confirmation-template',
    section: 'legacy'
  },
  BUN_ORDER_TEMPLATE: {
    label: 'Bunkering Order Email Template',
    type: 'text',
    defaultValue: '',
    description:
      'Email template for bunkering order requests. Supports {Stems}, {Agent}, {SamplingLocation} placeholders',
    windowGlobal: 'SX_BUN_ORDER_TEMPLATE',
    placeholder: 'e.g. Good day,\n\nPlease offer best on the following:\n\n{Stems}\n{Agent}\n{SamplingLocation}',
    section: 'legacy'
  },
  VOYAGE_COMPARISON_REPORT_PATH: {
    label: 'Voyage Comparison Report Path',
    type: 'text',
    defaultValue: '',
    description: 'Path to the voyage comparison report template',
    windowGlobal: 'sx_VoyageComparisonReportPath',
    placeholder: 'e.g. /sx/forms/tracker/reports/export-comparison-report',
    section: 'legacy'
  },
  VOYAGE_INSTRUCTIONS_REPORT_PATH: {
    label: 'Voyage Instructions Report Path',
    type: 'text',
    defaultValue: '',
    description: 'Path to the voyage instructions report template',
    windowGlobal: 'sx_VoyageInstructionsReportPath',
    placeholder: 'e.g. /sx/forms/tracker/reports/voyage-instructions',
    section: 'legacy'
  }
};

export type TenantSettingCode = keyof typeof TENANT_SETTINGS_REGISTRY;

export interface TenantSettingRecord {
  Id: string;
  Code: string;
  Name: string;
  Data: string;
  RecordVersion: number;
}

export interface TenantSettings {
  COMPANY_LOGO: string;
  FULLSTORY_ENABLED: string;
  ENABLE_BALTIC_QUESTIONNAIRES_IMPORT: string;
  SEND_BUNKERING_TO_PLAN: string;
  BUNKERING_PURCHASE_REPORT_PATH: string;
  BUNKERING_CONFIRMATION_REPORT_PATH: string;
  BUN_ORDER_TEMPLATE: string;
  VOYAGE_COMPARISON_REPORT_PATH: string;
  VOYAGE_INSTRUCTIONS_REPORT_PATH: string;
}

export const DEFAULT_TENANT_SETTINGS: TenantSettings = Object.fromEntries(
  Object.entries(TENANT_SETTINGS_REGISTRY).map(([key, def]) => [key, def.defaultValue])
) as unknown as TenantSettings;
